import React, { useEffect, useRef, useState } from 'react'
import { s3Static } from '../../../../utils/constants'
import Link from 'next/link'
import { FaArrowRight } from 'react-icons/fa'
import Image from 'next/image'

const airconProblems = [
  {
    title: "Leaking Water",
    href: "/sg/aircon-servicing-singapore/aircon-leaking-water",
    img: "blog/living/aircon-leaking-water.jpg",
    alt: "Aircon Leaking Water - StringsSG",
    desc: "Water leakage is usually caused by a clogged drain line or dirty filters. It can lead to dripping from the FCU unit.",
    buttonText: "Fix Water Leak",
    icon: "💧",
  },
  {
    title: "Cooling Problem",
    href: "/sg/aircon-servicing-singapore/aircon-not-cooling",
    img: "blog/living/cool-air-from-aircon.jpg",
    alt: "Fresh air from air conditioner",
    desc: "Aircon not blowing cold air? It may be due to low refrigerant gas, dirty coils, or a faulty condenser unit.",
    buttonText: "Fix A/C Cooling",
    icon: "🌡️",
  },
  {
    title: "Overheating",
    href: "/sg/aircon-servicing-singapore/aircon-overheating",
    img: "blog/living/dirty-aircon.jpg",
    alt: "Overheating Aircon - StringsSG",
    desc: "Overheating is often due to dirty filters, clogged coils, or long operation hours without servicing.",
    buttonText: "Fix A/C Overheating",
    icon: "🔥",
  },
  {
    title: "Condenser/Compressor Repair",
    href: "/sg/aircon-servicing-singapore/aircon-condenser-cleaning",
    img: "blog/living/aircon-condenser.jpg",
    alt: "Aircon Condenser Cleaning - StringsSG",
    desc: "A faulty or dirty condenser can cause poor cooling or complete system breakdown. Regular checks help avoid this.",
    buttonText: "Fix Compressor",
    icon: "⚙️",
  },
  {
    title: "PCB Replacement",
    href: "/sg/aircon-servicing-singapore/ac-printed-circuit-board-pcb",
    img: "blog/living/aircon-pcb-repair.jpg",
    alt: "Aircon PCB Repair - StringsSG",
    desc: "The PCB controls the FCU and compressor functions. A damaged PCB can stop your system from powering or responding.",
    buttonText: "PCB Repair & Replacement",
    icon: "🔌",
  },
  {
    title: "Noisy Aircon",
    href: "/sg/aircon-servicing-singapore/top-3-reasons-for-noisy-aircon-compressor-how-to-fix",
    img: "blog/living/aircon-blower.jpg",
    alt: "Noisy blower fan or condenser - StringsSG",
    desc: "Strange sounds from the A/C units may come from the blower, compressor, or motor. It often means worn-out parts.",
    buttonText: "Repair Noisy Aircon",
    icon: "🔊",
  },
]
export const AirconProblems = () => {
  const scrollRef = useRef(null)
  const [showArrow, setShowArrow] = useState(false)

  useEffect(() => {
    const el = scrollRef.current
    if (!el || window.innerWidth >= 768) return

    const updateArrow = () => {
      if (!el) return
      const hasMore = el.scrollLeft + el.offsetWidth < el.scrollWidth - 10
      setShowArrow(hasMore)
    }

    updateArrow()
    el.addEventListener('scroll', updateArrow)
    window.addEventListener('resize', updateArrow)

    return () => {
      el.removeEventListener('scroll', updateArrow)
      window.removeEventListener('resize', updateArrow)
    }
  }, [])

  return (
    <section className="relative py-8 md:py-14 overflow-hidden">
      {/* ─── Soft orange ambient blobs ─── */}
      <div className="relative max-w-6xl mx-auto px-4">
        {/* ─── Header ─── */}
        <div className="text-center mb-6">
          <h2 className="text-2xl md:text-4xl font-extrabold text-gray-900 tracking-tight">
            Common Problems We Fix
          </h2>
          <p className="mt-3 max-w-2xl mx-auto text-gray-500 text-sm md:text-base leading-relaxed">
            Most common problems you might face with your air conditioner like
            not enough cooling, leaking water, malfunctioning PCB,{' '}
            <Link href="/sg/aircon-servicing-singapore/aircon-drainage-pipe" className="text-orange-600 hover:underline font-medium">
              choked drainage pipe
            </Link>
            , noisy condenser or overheating FCU.
          </p>
        </div>

        {/* ─── Cards ─── */}
        <div
          ref={scrollRef}
          className="flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 scrollbar-hide"
          aria-label="List of common aircon problems"
          role="region"
        >
          {airconProblems.map((problem) => (
            <article
              key={problem.title}
              className="group w-[68vw] max-w-70 sm:w-[55vw] sm:max-w-75 md:w-auto md:max-w-none snap-start flex flex-col shrink-0"
              aria-labelledby={`${problem.title.toLowerCase().replace(/\s+/g, '-')}-heading`}
            >
              {/* Glass card */}
              <div className="h-full flex flex-col rounded-3xl overflow-hidden bg-white/50 backdrop-blur-xl border border-white/70 shadow-[0_8px_32px_rgba(251,146,60,0.08)] hover:shadow-[0_12px_48px_rgba(251,146,60,0.16)] transition-all duration-500 hover:-translate-y-1">
                {/* Image */}
                <div className="relative h-28 sm:h-40 overflow-hidden">
                  <Image
                    src={s3Static + problem.img}
                    fill
                    style={{ objectFit: 'cover' }}
                    placeholder="blur"
                    blurDataURL={s3Static + 'placeholder.png'}
                    sizes="(max-width: 768px) 75vw, (max-width: 1024px) 50vw, 33vw"
                    alt={problem.alt}
                    className="group-hover:scale-105 transition-transform duration-700 ease-out"
                  />
                  {/* Glass overlay badge */}
                  <div className="absolute top-3 left-3 bg-white/60 backdrop-blur-md border border-white/50 rounded-2xl px-3 py-1.5 shadow-sm">
                    <span className="text-lg mr-1">{problem.icon}</span>
                    <span className="text-xs font-semibold text-gray-700">{problem.title.split(' ')[0]}</span>
                  </div>
                  {/* Bottom gradient fade */}
                  <div className="absolute inset-x-0 bottom-0 h-16 bg-linear-to-t from-white/60 to-transparent" />
                </div>

                {/* Content */}
                <div className="flex flex-col grow p-5 md:p-6">
                  <h3
                    id={`${problem.title.toLowerCase().replace(/\s+/g, '-')}-heading`}
                    className="text-base md:text-lg font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors duration-300"
                  >
                    {problem.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed grow">
                    {problem.desc}
                  </p>

                  {/* CTA */}
                  <Link
                    href={problem.href}
                    prefetch={false}
                    role="link"
                    aria-label={`Learn more about ${problem.title}`}
                    className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-orange-600 group/link hover:text-orange-700 transition-colors"
                  >
                    {problem.buttonText}
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-100 group-hover/link:bg-orange-200 transition-colors">
                      <FaArrowRight className="text-[10px] text-orange-600 group-hover/link:translate-x-0.5 transition-transform" />
                    </span>
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* ─── Mobile scroll indicator ─── */}
        {showArrow && (
          <div className="absolute right-4 top-[65%] z-20 block md:hidden pointer-events-none animate-pulse">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/70 backdrop-blur-md border border-orange-200/50 shadow-lg shadow-orange-200/20">
              <FaArrowRight className="text-sm text-orange-500" />
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
